import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DocumentNode } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client";
import SideSheetModal from "@/components/SideSheetModal";
import TextField from "@/components/TextField";
import Icon from "@/components/Icon";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import ListItem from "@/components/ListItem/ListItem";
import SubSectionHeading from "@/components/SubSectionHeading/SubSectionHeading";
import {
  useAlgoliaSearch,
  AlgoliaSearchResults,
  InlineSearchResults,
  type PersonHit,
  type FacilityHit,
  type GlobalHit,
  type EventHit,
  createGlobalSearchUiState,
  renderGlobalHit,
  getGlobalDisplayName,
  isGlobalExactMatch,
  filterGlobalSearchHits,
  renderEventHit,
  getEventDisplayName
} from "@/components/AlgoliaSearch";
import { showSnackbar } from "@/lib/snackbarUtils";
import {
  BULK_CREATE_REFERENCES_MUTATION,
  TYPENAME_TO_REFERENCEABLE_TYPE,
  type BulkCreateReferencesResponse,
  type BackofficeCRMBulkReferencesInput
} from "@/features/global/components/NotesTable/mutations";
import {
  getReferenceIcon,
  getReferenceTypeLabel,
  getReferenceSupportingText
} from "@/features/global/components/ReferencesTable/types";
import {
  useReferencePickerItems,
  type ReferenceLookupContext,
  type ReferencePickerItem
} from "../_hooks/useReferencePickerItems";
import { EVENTS_FOR_REFERENCE_SEARCH_QUERY, type EventSearchNode } from "../_hooks/eventsForReferenceSearchQuery";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS } from "@/pages/tasks/_utils/amplitude";

const EVENT_SEARCH_LIMIT = 15;

function createPersonFacilitySearchUiState(indexName: string) {
  const uiState = createGlobalSearchUiState(indexName);
  return {
    [indexName]: {
      configure: {
        ...uiState[indexName].configure,
        filters: "object_type:Person OR object_type:Facility"
      }
    }
  };
}

function isPersonHit(hit: GlobalHit): hit is PersonHit {
  return (
    hit.object_type === "Person" &&
    (("pga_id" in hit && typeof hit.pga_id === "string") || ("legacy_id" in hit && typeof hit.legacy_id === "string"))
  );
}

function isFacilityHit(hit: GlobalHit): hit is FacilityHit {
  return hit.object_type === "Facility" && typeof hit.facility_id === "string";
}

function getLookupContext(hit: GlobalHit): ReferenceLookupContext {
  if (isPersonHit(hit)) {
    const pgaId = hit.pga_id || hit.legacy_id;
    return pgaId ? { type: "Person", id: pgaId } : null;
  }

  if (isFacilityHit(hit)) {
    return { type: "Facility", id: hit.facility_id };
  }

  return null;
}

function mapEventToHit(event: EventSearchNode): EventHit {
  return {
    objectID: event.id,
    id: event.id,
    title: event.title,
    slug: event.slug,
    startsAt: event.startsAt,
    endsAt: event.endsAt,
    city: event.city ?? undefined,
    state: event.state ?? undefined,
    facilityName: event.facility?.name,
    object_type: "Event"
  };
}

function getEventLookupContext(event: EventSearchNode): ReferenceLookupContext {
  return {
    type: "Event",
    id: event.id,
    slug: event.slug,
    title: event.title,
    status: event.status,
    startsAt: event.startsAt
  };
}

interface AddTaskReferenceSideSheetProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  existingReferenceIds: string[];
  refetchQueries?: DocumentNode[];
}

export default function AddTaskReferenceSideSheet({
  open,
  onClose,
  taskId,
  existingReferenceIds,
  refetchQueries = []
}: AddTaskReferenceSideSheetProps) {
  const indexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;
  const { trackEvent } = useAmplitude();

  const [selectedLookupContext, setSelectedLookupContext] = useState<ReferenceLookupContext>(null);
  const [selectedItems, setSelectedItems] = useState<ReferencePickerItem[]>([]);
  const [referenceSearch, setReferenceSearch] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [debouncedEventSearch, setDebouncedEventSearch] = useState("");
  const [lookupFocused, setLookupFocused] = useState(false);
  const [eventHighlightedIndex, setEventHighlightedIndex] = useState(-1);
  const referenceSearchRef = useRef<HTMLInputElement>(null);
  const eventResultsRef = useRef<EventSearchNode[]>([]);
  const eventHighlightedIndexRef = useRef(-1);
  const showEventOnlyKeyboardRef = useRef(false);
  const handleSelectEventRef = useRef<(event: EventSearchNode) => void>(() => {});

  const {
    query: lookupQuery,
    displayValue,
    results: lookupResults,
    isLoading: lookupLoading,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear: handleLookupClear,
    handleItemSelected: handleLookupItemSelected,
    setIsFocused,
    setDisplayValue,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useAlgoliaSearch<GlobalHit>({
    indexName,
    hitsPerPage: 15,
    initialUiState: createPersonFacilitySearchUiState(indexName),
    getDisplayValue: getGlobalDisplayName,
    isExactMatch: isGlobalExactMatch,
    filterHits: filterGlobalSearchHits,
    onSelect: (hit) => {
      const context = getLookupContext(hit);
      if (context) {
        setSelectedLookupContext(context);
      }
    },
    onSubmit: () => {
      if (!showEventOnlyKeyboardRef.current) return;
      const idx = eventHighlightedIndexRef.current;
      const events = eventResultsRef.current;
      if (idx >= 0 && idx < events.length) {
        handleSelectEventRef.current(events[idx]);
      }
    }
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEventSearch(lookupQuery.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [lookupQuery]);

  const { data: eventsData, loading: eventsLoading } = useQuery(EVENTS_FOR_REFERENCE_SEARCH_QUERY, {
    variables: { query: { title: debouncedEventSearch }, first: EVENT_SEARCH_LIMIT },
    skip: !debouncedEventSearch || !!selectedLookupContext
  });

  const eventResults = useMemo(() => eventsData?.events?.edges?.map(({ node }) => node) ?? [], [eventsData]);
  eventResultsRef.current = eventResults;
  eventHighlightedIndexRef.current = eventHighlightedIndex;

  const {
    items: allPickerItems,
    loading: referencesLoading,
    error: referencesError,
    truncated
  } = useReferencePickerItems(selectedLookupContext);

  const [bulkCreateReferences, { loading: saving }] = useMutation<
    BulkCreateReferencesResponse,
    { input: BackofficeCRMBulkReferencesInput }
  >(BULK_CREATE_REFERENCES_MUTATION, { refetchQueries });

  const existingIds = useMemo(() => new Set(existingReferenceIds), [existingReferenceIds]);
  const selectedIds = useMemo(() => new Set(selectedItems.map((i) => i.id)), [selectedItems]);

  const filteredItems = useMemo(() => {
    const available = allPickerItems.filter((item) => !existingIds.has(item.id) && !selectedIds.has(item.id));
    if (!referenceSearch.trim()) return available;
    const term = referenceSearch.toLowerCase();
    return available.filter(
      (item) =>
        item.displayName.toLowerCase().includes(term) ||
        getReferenceTypeLabel(item.__typename, item.notableType).toLowerCase().includes(term)
    );
  }, [allPickerItems, existingIds, selectedIds, referenceSearch]);

  useEffect(() => {
    if (selectedLookupContext) {
      requestAnimationFrame(() => {
        referenceSearchRef.current?.focus();
      });
    }
  }, [selectedLookupContext]);

  const handleToggleItem = useCallback((item: ReferencePickerItem) => {
    setSelectedItems((prev) => {
      const exists = prev.find((i) => i.id === item.id);
      if (exists) return prev.filter((i) => i.id !== item.id);
      return [...prev, item];
    });
  }, []);

  const handleClearLookup = useCallback(() => {
    handleLookupClear();
    setSelectedLookupContext(null);
    setSelectedItems([]);
    setReferenceSearch("");
    setSaveError(null);
    setDebouncedEventSearch("");
    setEventHighlightedIndex(-1);
  }, [handleLookupClear]);

  const handleSelectEvent = useCallback(
    (event: EventSearchNode) => {
      setDisplayValue(getEventDisplayName(mapEventToHit(event)));
      handleQueryChange("");
      setDebouncedEventSearch("");
      setEventHighlightedIndex(-1);
      setSelectedLookupContext(getEventLookupContext(event));
    },
    [handleQueryChange, setDisplayValue]
  );
  handleSelectEventRef.current = handleSelectEvent;

  const handleClose = useCallback(() => {
    handleClearLookup();
    onClose();
  }, [handleClearLookup, onClose]);

  const handleSave = async () => {
    if (selectedItems.length === 0) return;
    setSaveError(null);

    const references = selectedItems
      .map((item) => ({
        referenceableId: item.id,
        referenceableType: TYPENAME_TO_REFERENCEABLE_TYPE[item.__typename]
      }))
      .filter((r): r is BackofficeCRMBulkReferencesInput["references"][number] => r.referenceableType !== undefined);

    if (references.length === 0) return;

    try {
      const result = await bulkCreateReferences({
        variables: {
          input: {
            referencedById: taskId,
            referencedByType: "TASK",
            references
          }
        }
      });

      const payload = result.data?.bulkCreateReferences;
      if (payload?.success) {
        trackEvent(TASK_ANALYTICS_EVENTS.referenceAdded, {
          taskId,
          source: "task_detail",
          referenceCount: references.length,
          referenceTypes: Array.from(new Set(references.map((reference) => reference.referenceableType))).sort()
        });
        showSnackbar(selectedItems.length === 1 ? "Reference added" : "References added");
        handleClose();
      } else {
        setSaveError(payload?.message || "Failed to save references. Please try again.");
      }
    } catch {
      setSaveError("A network error occurred while saving references. Please try again.");
    }
  };

  const hasActiveLookupQuery = !selectedLookupContext && lookupQuery.trim() !== "";
  const eventSearchSynced = debouncedEventSearch === lookupQuery.trim();
  const selectedLookupLabel = selectedLookupContext?.type ?? "Person";
  const referenceSearchLabel = `${selectedLookupLabel} References`;
  const emptyReferencesText = `No references found for this ${selectedLookupLabel.toLowerCase()}`;
  const showAlgoliaResults = hasActiveLookupQuery && !lookupLoading && lookupResults.length > 0;
  const showEventResults = hasActiveLookupQuery && eventSearchSynced && !eventsLoading && eventResults.length > 0;
  const showEventOnlyKeyboard = showEventResults && !showAlgoliaResults;
  showEventOnlyKeyboardRef.current = showEventOnlyKeyboard;
  const showLookupLoading =
    hasActiveLookupQuery &&
    (lookupLoading || eventsLoading || !eventSearchSynced) &&
    lookupResults.length === 0 &&
    (!eventSearchSynced || eventResults.length === 0);
  const showLookupEmpty =
    hasActiveLookupQuery &&
    eventSearchSynced &&
    !lookupLoading &&
    !eventsLoading &&
    lookupResults.length === 0 &&
    eventResults.length === 0;

  useEffect(() => {
    setEventHighlightedIndex(showEventResults ? 0 : -1);
  }, [showEventResults, eventResults]);

  useEffect(() => {
    if (!lookupFocused || !showEventOnlyKeyboard) return;

    const handler = (e: globalThis.KeyboardEvent) => {
      const events = eventResultsRef.current;
      if (events.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setEventHighlightedIndex((prev) => (prev + 1) % events.length);
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setEventHighlightedIndex((prev) => (prev - 1 + events.length) % events.length);
      }
    };

    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [lookupFocused, showEventOnlyKeyboard]);

  const lookupTrailingIcon =
    displayValue || lookupQuery ? (
      <button
        className="cursor-pointer flex items-center h-full"
        onClick={handleClearLookup}
        aria-label="Clear search query"
      >
        <Icon name="cancel" />
      </button>
    ) : undefined;

  const referenceTrailingIcon = referenceSearch ? (
    <button
      className="cursor-pointer flex items-center h-full"
      onClick={() => setReferenceSearch("")}
      aria-label="Clear reference search"
    >
      <Icon name="cancel" />
    </button>
  ) : undefined;

  const actions = selectedLookupContext ? (
    <>
      <Button variant="text" onClick={handleClose} disabled={saving}>
        Cancel
      </Button>
      <Button
        variant="filled"
        onClick={handleSave}
        disabled={saving || selectedItems.length === 0}
        testId="save-task-references-button"
      >
        {saving ? "Saving..." : "Save"}
      </Button>
    </>
  ) : null;

  return (
    <SideSheetModal
      isSidebarOpen={open}
      setSidebarOpen={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title="Add Reference"
      actions={actions}
    >
      <p className="mt-4 text-body-large text-sys-brand-on-surface-variant">
        Search for a person, facility, or event to see available options, then select one or more items to link as
        references.
      </p>

      <div className="mt-6" ref={searchBoxRef}>
        <TextField
          label="Person, Facility, or Event"
          value={displayValue || lookupQuery}
          onChangeText={(value) => {
            if (selectedLookupContext) {
              handleClearLookup();
            }
            setDisplayValue(undefined);
            handleQueryChange(value);
          }}
          onFocus={() => {
            setLookupFocused(true);
            setIsFocused(true);
          }}
          onBlur={() => {
            setLookupFocused(false);
            setIsFocused(false);
          }}
          leadingIcon="search"
          trailingIcon={lookupTrailingIcon}
          variant="outlined"
          className="w-full"
          data-testid="reference-source-search-input"
        />
      </div>

      {showLookupLoading && <div className="mt-4 py-3 text-center text-sys-brand-on-surface-variant">Searching...</div>}

      {showLookupEmpty && (
        <div className="mt-4 py-3 px-4 text-center text-body-medium-semibold text-extended-pgasilver-brand-on-color bg-extended-pgasilver-brand-color rounded-lg">
          No results found
        </div>
      )}

      {showAlgoliaResults && (
        <AlgoliaSearchResults
          results={lookupResults}
          query={lookupQuery}
          isLoading={false}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          handleItemSelected={handleLookupItemSelected}
          itemRefs={itemRefs}
          resultsContainerRef={resultsContainerRef}
          renderHit={renderGlobalHit}
          isExactMatch={isGlobalExactMatch}
          className="mt-4"
        />
      )}

      {showEventResults && (
        <div className="mt-4" data-testid="event-reference-search-results">
          {showAlgoliaResults && <SubSectionHeading className="mb-2">Events</SubSectionHeading>}
          <InlineSearchResults
            results={eventResults}
            isLoading={false}
            getKey={(event) => event.id}
            highlightedIndex={showEventOnlyKeyboard ? eventHighlightedIndex : -1}
            onHighlightChange={showEventOnlyKeyboard ? setEventHighlightedIndex : undefined}
            renderItem={(event) => renderEventHit(mapEventToHit(event), () => handleSelectEvent(event))}
          />
        </div>
      )}

      {selectedLookupContext && (
        <>
          {saveError && (
            <Banner
              variant="error"
              description={saveError}
              inline
              closable
              onClose={() => setSaveError(null)}
              testId="save-references-error"
              className="mt-4"
            />
          )}

          {referencesError && (
            <Banner
              variant="error"
              description={`Unable to load ${referenceSearchLabel.toLowerCase()}. Please try again.`}
              inline
              className="mt-4"
            />
          )}

          <div className="mt-6">
            <TextField
              ref={referenceSearchRef}
              label={referenceSearchLabel}
              value={referenceSearch}
              onChangeText={setReferenceSearch}
              leadingIcon="search"
              trailingIcon={referenceTrailingIcon}
              variant="outlined"
              className="w-full"
              supportingText={truncated ? "Not all references may be visible at once, search to narrow" : undefined}
              data-testid="reference-search-input"
            />
          </div>

          {selectedItems.length > 0 && (
            <div className="sticky -top-px z-10 mt-4 pt-px pb-2 bg-sys-brand-surface-container-low">
              <SubSectionHeading>Selected ({selectedItems.length})</SubSectionHeading>
            </div>
          )}

          {selectedItems.length > 0 && (
            <div className="space-y-1" data-testid="selected-references-list">
              {selectedItems.map((item) => (
                <ListItem
                  key={`${item.__typename}-${item.id}`}
                  compact
                  useAvatar
                  iconName={getReferenceIcon(item.__typename)}
                  headline={item.displayName}
                  supportingText={getReferenceSupportingText(item)}
                  useCheckbox
                  checkboxChecked={true}
                  onCheckboxChange={() => handleToggleItem(item)}
                  checkboxName={`ref-${item.id}`}
                />
              ))}
            </div>
          )}

          <div className="mt-4">
            {referencesLoading ? (
              <div className="py-3 text-center text-sys-brand-on-surface-variant">Loading...</div>
            ) : referencesError ? null : filteredItems.length === 0 ? (
              <div className="py-3 px-4 text-center text-body-medium-semibold text-extended-pgasilver-brand-on-color bg-extended-pgasilver-brand-color rounded-lg">
                {referenceSearch.trim()
                  ? "No matching references found"
                  : allPickerItems.length === 0
                    ? emptyReferencesText
                    : "All available references have been selected"}
              </div>
            ) : (
              <div>
                <SubSectionHeading className="mb-2">Available</SubSectionHeading>
                {filteredItems.map((item) => (
                  <ListItem
                    key={`${item.__typename}-${item.id}`}
                    compact
                    useAvatar
                    iconName={getReferenceIcon(item.__typename)}
                    headline={item.displayName}
                    supportingText={getReferenceSupportingText(item)}
                    useCheckbox
                    checkboxChecked={false}
                    onCheckboxChange={() => handleToggleItem(item)}
                    checkboxName={`ref-${item.id}`}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </SideSheetModal>
  );
}
